import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  TrendingUp,
  Zap,
  ShieldCheck,
  ArrowRight,
  Circle,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CURRENT = {
  sleep_hours: 5,
  steps: 3000,
  diet: "processed",
  stress_level: 8,
  water_glasses: 4,
  exercise_frequency: "rarely",
  smoking: "none",
  alcohol: "weekly",
};

const DEFAULT_IMPROVED = {
  sleep_hours: 7.5,
  steps: 8000,
  diet: "balanced",
  stress_level: 4,
  water_glasses: 8,
  exercise_frequency: "4x/week",
  smoking: "none",
  alcohol: "none",
};

const DIETS = ["processed", "mostly-veg", "balanced", "high-protein", "vegan"];
const FREQS = ["rarely", "1x/week", "2x/week", "3x/week", "4x/week", "daily"];
const SUB = ["none", "occasional", "weekly", "daily"];

export default function FutureSimulator() {
  const [age, setAge] = useState(28);
  const [gender, setGender] = useState("male");
  const [goals, setGoals] = useState("Sleep better, lose 4 kg");
  const [conditions, setConditions] = useState("");
  const [current, setCurrent] = useState(DEFAULT_CURRENT);
  const [improved, setImproved] = useState(DEFAULT_IMPROVED);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/simulator/forecast`, {
        age,
        gender,
        goals,
        conditions,
        current,
        improved,
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Simulation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(52,211,153,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="sim-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/dashboard"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
            Chapter 07 · Future Simulator
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            See tomorrow.{" "}
            <span className="italic text-emerald-300">Today.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Compare your current lifestyle with a small, realistic upgrade and
            see how your body typically responds at 30, 90 and 365 days.
          </p>
        </div>

        {/* Inputs */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LifestyleForm
            title="Your current lifestyle"
            testId="sim-current"
            values={current}
            onChange={setCurrent}
            accent="#f87171"
          />
          <LifestyleForm
            title="Your improved lifestyle"
            testId="sim-improved"
            values={improved}
            onChange={setImproved}
            accent="#34d399"
          />
        </div>

        <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Age" testId="sim-age">
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(+e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
          <Field label="Gender" testId="sim-gender">
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Goals" testId="sim-goals">
            <input
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
          <Field label="Conditions (optional)" testId="sim-conditions">
            <input
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              placeholder="e.g., PCOS, hypertension"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            data-testid="sim-run"
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-6 py-3 text-sm hover:bg-cyan-100 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Simulating your future
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Run the simulation
              </>
            )}
          </button>
          <div className="text-[11px] text-zinc-500 leading-relaxed max-w-md">
            Non-diagnostic educational projection based on public-health
            guidance. Actual outcomes vary.
          </div>
        </div>

        {result && (
          <SimulationResult data={result} />
        )}
      </div>
    </div>
  );
}

const LifestyleForm = ({ title, values, onChange, accent, testId }) => {
  const set = (k, v) => onChange({ ...values, [k]: v });
  return (
    <div
      data-testid={testId}
      className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
    >
      <div className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: accent }}
        />
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          {title}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Field label="Sleep (hrs)">
          <input
            type="number"
            step="0.5"
            value={values.sleep_hours ?? ""}
            onChange={(e) => set("sleep_hours", +e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          />
        </Field>
        <Field label="Steps / day">
          <input
            type="number"
            value={values.steps ?? ""}
            onChange={(e) => set("steps", +e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          />
        </Field>
        <Field label="Diet">
          <select
            value={values.diet}
            onChange={(e) => set("diet", e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          >
            {DIETS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Exercise">
          <select
            value={values.exercise_frequency}
            onChange={(e) => set("exercise_frequency", e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          >
            {FREQS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Stress (1-10)">
          <input
            type="number"
            min="1"
            max="10"
            value={values.stress_level ?? ""}
            onChange={(e) => set("stress_level", +e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          />
        </Field>
        <Field label="Water (glasses)">
          <input
            type="number"
            value={values.water_glasses ?? ""}
            onChange={(e) => set("water_glasses", +e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          />
        </Field>
        <Field label="Smoking">
          <select
            value={values.smoking}
            onChange={(e) => set("smoking", e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          >
            {SUB.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Alcohol">
          <select
            value={values.alcohol}
            onChange={(e) => set("alcohol", e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          >
            {SUB.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
};

const Field = ({ label, testId, children }) => (
  <label data-testid={testId} className="block">
    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

const SimulationResult = ({ data }) => (
  <motion.div
    data-testid="sim-result"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="mt-12"
  >
    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 p-8 sm:p-10">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        The verdict
      </div>
      <p className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-white leading-snug">
        {data.verdict}
      </p>
    </div>

    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <SummaryCard s={data.current} accent="#f87171" />
      <SummaryCard s={data.improved} accent="#34d399" />
    </div>

    {/* Timeline */}
    <div className="mt-10">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        The timeline
      </div>
      <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold">
        Here&apos;s how your body typically responds.
      </h3>
      <div className="mt-6 flex flex-col gap-3">
        {data.timeline.map((tp, i) => (
          <motion.div
            key={i}
            data-testid={`sim-timeline-${i}`}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-cyan-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                  {tp.horizon}
                </div>
                <div className="text-sm text-zinc-200 mt-1">
                  {tp.metric_summary}
                </div>
              </div>
            </div>
            {tp.changes?.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tp.changes.map((c, j) => (
                  <div
                    key={j}
                    className="rounded-xl border border-white/10 bg-black/30 px-3 py-2"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-xs text-zinc-300">{c.metric}</div>
                      <div className="text-sm font-semibold text-emerald-300 tabular-nums">
                        {c.delta}
                      </div>
                    </div>
                    {c.note && (
                      <div className="text-[11px] text-zinc-500 mt-0.5">{c.note}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>

    {/* Risks + Habits */}
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          <ShieldCheck className="w-3.5 h-3.5" />
          Risks reduced (long-term)
        </div>
        <ul className="mt-4 flex flex-col gap-2">
          {data.risks_reduced.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
              <span className="text-cyan-300 mt-1">•</span>
              {r}
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          <Zap className="w-3.5 h-3.5" />
          Top habits to build first
        </div>
        <ul className="mt-4 flex flex-col gap-2">
          {data.top_habits.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
              <span className="text-emerald-300 mt-1">•</span>
              {h}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>
);

const SummaryCard = ({ s, accent }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: accent }}>
      <Circle className="w-2 h-2 fill-current" />
      {s.label}
    </div>
    <div className="mt-3 flex items-baseline gap-2">
      <div className="font-display text-5xl font-semibold tabular-nums text-white">
        {s.score}
      </div>
      <div className="text-sm text-zinc-500">/100</div>
    </div>
    <ul className="mt-4 flex flex-col gap-1.5">
      {s.signals.map((sig, i) => (
        <li key={i} className="text-xs text-zinc-400">
          — {sig}
        </li>
      ))}
    </ul>
  </div>
);
